import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
    CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContentContainer from '../../components/StepContentContainer';
import DocumentScanner from '../../components/DocumentScanner';
import Confetti from 'react-confetti';

export default function MesseDemoPage() {
    const [step, setStep] = useState(0);
    const [backImage, setBackImage] = useState(null);
    const [imageConfirmed, setImageConfirmed] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [showDataBanner, setShowDataBanner] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    const abrechnungsbetrag = 249.30;

    const patient = {
        name: "Dora Dachs",
        geburtsdatum: "09.06.1982",
        krankenkasse: "AOK Bayern DAV",
        versichertennummer: "D049241296",
        status: "3000",
        adresse: "Nebenstraße 13, 80336 München",
        kostenträgerkennung: "108310400",
        ikLeistungserbringer: "123456789",
        Verordungsdatum: "10.04.2025"
    };

    const behandlungen = [
        { datum: "14.04.2025", maßnahme: "KG - Warmpackung" },
        { datum: "21.04.2025", maßnahme: "KG - Warmpackung" },
        { datum: "17.04.2025", maßnahme: "KG - Warmpackung" },
        { datum: "24.04.2025", maßnahme: "KG - Warmpackung" },
        { datum: "28.04.2025", maßnahme: "KG - Warmpackung" },
        { datum: "01.05.2025", maßnahme: "KG - Warmpackung" },
    ];

    const steps = [
        'Rückseite erfassen',
        'Scan bestätigen',
        'Datenprüfung',
        'Abrechnung starten',
        'Abschluss'
    ];

    const next = () => setStep((s) => s + 1);
    const restart = () => {
        setStep(0);
        setBackImage(null);
        setImageConfirmed(false);
        setLoadingData(false);
        setShowDataBanner(false);
        setShowConfetti(false);
    };

    const StartScreen = () => (
        <Box textAlign="center" sx={{ mt: 10 }}>
            <Typography variant="h4" gutterBottom>
                Willkommen bei heyRobin VO-Check
            </Typography>
            <Button variant="contained" size="large" onClick={next}>
                Verordnung abrechnen
            </Button>
        </Box>
    );

    const Schritt1 = () => (
        <StepContentContainer>
            <Typography variant="h5" gutterBottom>
                Schritt 1: Rückseite scannen
            </Typography>
            {!backImage ? (
                <DocumentScanner onCaptureComplete={(dataUrl) => {
                    setBackImage(dataUrl);
                    next();
                }} />
            ) : (
                <Typography>Scan abgeschlossen.</Typography>
            )}
        </StepContentContainer>
    );

    const Schritt2 = () => (
        <StepContentContainer>
            <Typography variant="h5" gutterBottom>
                Schritt 2: Scan bestätigen
            </Typography>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body1" gutterBottom>
                    Bitte prüfen Sie das erfasste Bild:
                </Typography>
                <img
                    src={backImage}
                    alt="Gescanntes Rezept Rückseite"
                    style={{ maxWidth: '100%', border: '1px solid #ccc', borderRadius: 8 }}
                />
                <Box mt={2}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setImageConfirmed(true);
                            setLoadingData(true);
                            next();
                            setTimeout(() => {
                                setLoadingData(false);
                                setShowDataBanner(true);
                            }, 5000); // 10 Sekunden
                        }}
                    >
                        Bild bestätigen
                    </Button>
                </Box>
            </Box>
        </StepContentContainer>
    );

    const Schritt3 = () => (
        <StepContentContainer>
            <Typography variant="h5" gutterBottom>
                Schritt 3: Datenprüfung
            </Typography>

            {loadingData ? (
                <Box textAlign="center" sx={{ mt: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Verordnung wird analysiert...</Typography>
                </Box>
            ) : (
                <>
                    {showDataBanner && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            ✅ Passende Verordnung gefunden. Daten wurden erfolgreich zusammengeführt.
                        </Alert>
                    )}

                    <Box sx={{ mb: 3, textAlign: 'left' }}>
                        <Typography variant="h6">Patienteninformationen</Typography>
                        <ul>
                            <li><strong>Name:</strong> {patient.name}</li>
                            <li><strong>Geburtsdatum:</strong> {patient.geburtsdatum}</li>
                            <li><strong>Krankenkasse:</strong> {patient.krankenkasse}</li>
                            <li><strong>Versichertennummer:</strong> {patient.versichertennummer}</li>
                            <li><strong>Status:</strong> {patient.status}</li>
                            <li><strong>Adresse:</strong> {patient.adresse}</li>
                            <li><strong>Kostenträgerkennung:</strong> {patient.kostenträgerkennung}</li>
                            <li><strong>IK Leistungserbringer:</strong> {patient.ikLeistungserbringer}</li>
                            <li><strong>Verordnungsdatum:</strong> {patient.Verordungsdatum}</li>
                        </ul>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6">Erbrachte Leistungen</Typography>
                        <Table size="small" component={Paper}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Datum</TableCell>
                                    <TableCell>Maßnahme</TableCell>
                                    <TableCell>Leistungserbringer</TableCell>
                                    <TableCell>Versicherter</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {behandlungen.map((b, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{b.datum}</TableCell>
                                        <TableCell>{b.maßnahme}</TableCell>
                                        <TableCell><CheckIcon color="success" /></TableCell>
                                        <TableCell><CheckIcon color="success" /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    <Alert severity="success" sx={{ mb: 3 }}>
                        Alle Daten korrekt – bereit zur Abrechnung.
                    </Alert>

                    <Box textAlign="right">
                        <Button variant="contained" onClick={next}>
                            Weiter zur Abrechnung
                        </Button>
                    </Box>
                </>
            )}
        </StepContentContainer>
    );

    const Schritt4 = () => (
        <StepContentContainer>
            <Typography variant="h5" gutterBottom>
                Schritt 4: Abrechnung
            </Typography>
            <Box textAlign="center" sx={{ mt: 5 }}>
                <Typography variant="h6" gutterBottom>
                    Abrechnungsbetrag: <strong>{abrechnungsbetrag.toFixed(2)} €</strong>
                </Typography>
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    sx={{ mt: 2, fontSize: '1.2rem', py: 2, px: 4 }}
                    onClick={() => {
                        setShowConfetti(true);
                        next();
                    }}
                >
                    Verordnung jetzt abrechnen
                </Button>
            </Box>
        </StepContentContainer>
    );

    const Schritt5 = () => (
        <StepContentContainer>
            {showConfetti && <Confetti />}
            <Typography variant="h4" color="green" gutterBottom>
                ✔ Prüfung und Übermittlung abgeschlossen!
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
                Bitte versenden Sie jetzt Ihre Originalverordnung an unser Rechenzentrum.
            </Typography>
            <Button variant="contained" onClick={restart}>
                Neue Verordnung erfassen
            </Button>
        </StepContentContainer>
    );

    const renderStep = () => {
        switch (step) {
            case 0: return <StartScreen />;
            case 1: return <Schritt1 />;
            case 2: return <Schritt2 />;
            case 3: return <Schritt3 />;
            case 4: return <Schritt4 />;
            case 5: return <Schritt5 />;
            default: return <div>Unbekannter Schritt</div>;
        }
    };

    return (
        <div>
            {step > 0 && (
                <Box sx={{ mt: '3rem', maxWidth: '800px', mx: 'auto' }}>
                    <Stepper activeStep={step - 1} alternativeLabel>
                        {steps.map((label, index) => (
                            <Step key={index}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
            )}
            <Container sx={{ mt: 2 }}>{renderStep()}</Container>
        </div>
    );
}
